/**
 * ORCA360 Part B - TEM (Threat & Error Management) Overview Component
 * Displays threats, errors, resilience factors, and UAS events
 */


export interface Threat {
  category: string;
  description: string;
  primaryCompetency: string;
  secondaryCompetencies: string[];
  management?: string;
  evidence?: string;
}

export interface Error {
  type: string;
  classification: string;
  description: string;
  linkedThreats?: string[];
  ledToUAS: boolean;
  evidence?: string;
}

export interface ResilienceFactor {
  description: string;
  competencies: string[];
  evidence?: string;
}

export interface UAS {
  description: string;
  severity: string;
  recovery?: string;
  evidence?: string;
}

export interface PartBData {
  operationalContext: string;
  threats: Threat[];
  errors: Error[];
  resilienceFactors: ResilienceFactor[];
  uasEvents: UAS[];
  managementSummary: string;
}

interface Props {
  data: PartBData;
}

const COMPETENCY_LABELS: Record<string, string> = {
  PRO: 'Application of Procedures',
  COM: 'Communication',
  FPA: 'Flight Path Mgmt - Auto',
  FPM: 'Flight Path Mgmt - Manual',
  KNO: 'Knowledge',
  LTW: 'Leadership & Teamwork',
  PSD: 'Prob. Solving & Decision Making',
  SAW: 'Situation Awareness',
  WLM: 'Workload Management',
};

export function ORCA360PartB({ data }: Props) {
  const getManagementColor = (management?: string): string => {
    switch (management) {
      case 'Managed':
        return 'text-green-700 bg-green-100';
      case 'Mismanaged':
        return 'text-red-700 bg-red-100';
      case 'Unmanaged':
        return 'text-yellow-700 bg-yellow-100';
      default:
        return 'text-gray-700 bg-gray-100';
    }
  };

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'Serious':
        return 'text-red-700 bg-red-100';
      case 'Moderate':
        return 'text-yellow-700 bg-yellow-100';
      case 'Minor':
        return 'text-green-700 bg-green-100';
      default:
        return 'text-gray-700 bg-gray-100';
    }
  };

  return (
    <div className="bg-white sm:rounded-lg sm:shadow-md p-2 sm:p-6 border-y sm:border border-slate-200 sm:border-gray-200">
      <h2 className="text-base sm:text-lg font-bold text-navy mb-2 sm:mb-4 border-b border-gray-200 pb-2">
        Part B — Threat & Error Management (TEM) Overview
      </h2>

      {/* Operational Context */}
      <div className="mb-3 sm:mb-6">
        <p className="text-sm text-gray-700 italic bg-gray-50 p-2 sm:p-3 rounded">
          {data.operationalContext || 'No operational context provided'}
        </p>
      </div>

      {/* Threats - Desktop Table */}
      <div className="mb-3 sm:mb-6">
        <h3 className="text-md font-semibold text-navy mb-2 sm:mb-3">TEM Analysis - Threats</h3>
        {data.threats.length > 0 ? (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600 border-b">Threat Category</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600 border-b">Primary Competency</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600 border-b">Secondary</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600 border-b">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.threats.map((threat, idx) => (
                    <tr key={idx} className="border-b border-gray-100">
                      <td className="px-3 py-2">
                        <div className="font-medium text-navy">{threat.category}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{threat.description}</div>
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                          {threat.primaryCompetency}
                        </span>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {COMPETENCY_LABELS[threat.primaryCompetency] || threat.primaryCompetency}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {threat.secondaryCompetencies.map((comp, i) => (
                            <span key={i} className="inline-block px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                              {comp}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getManagementColor(threat.management)}`}>
                          {threat.management || '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="sm:hidden space-y-1.5">
              {data.threats.map((threat, idx) => (
                <div key={idx} className="p-2 bg-amber-50 border border-amber-200 rounded">
                  <div className="flex items-start justify-between mb-1">
                    <span className="text-sm font-medium text-navy">{threat.category}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getManagementColor(threat.management)}`}>
                      {threat.management || '—'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mb-1">{threat.description}</p>
                  <div className="flex flex-wrap gap-1">
                    <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium">
                      {threat.primaryCompetency}
                    </span>
                    {threat.secondaryCompetencies.map((comp, i) => (
                      <span key={i} className="px-1 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px]">
                        {comp}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-500 italic">No threats identified</p>
        )}
      </div>

      {/* Errors & Resilience */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-6 mb-3 sm:mb-6">
        {/* Errors */}
        <div>
          <h3 className="text-md font-semibold text-navy mb-2 sm:mb-3">Errors</h3>
          {data.errors.length > 0 ? (
            <div className="space-y-1.5 sm:space-y-2">
              {data.errors.map((error, idx) => (
                <div key={idx} className="p-2 sm:p-3 bg-red-50 border border-red-100 rounded">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-red-700">{error.type} ({error.classification})</span>
                    {error.ledToUAS && (
                      <span className="text-[10px] sm:text-xs px-1.5 py-0.5 bg-red-200 text-red-800 rounded font-medium">
                        Led to UAS
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700">{error.description}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">No errors identified</p>
          )}
        </div>

        {/* Resilience Factors */}
        <div>
          <h3 className="text-md font-semibold text-navy mb-2 sm:mb-3">Resilience Factors</h3>
          {data.resilienceFactors.length > 0 ? (
            <div className="space-y-1.5 sm:space-y-2">
              {data.resilienceFactors.map((factor, idx) => (
                <div key={idx} className="p-2 sm:p-3 bg-green-50 border border-green-100 rounded">
                  <p className="text-sm text-gray-700">{factor.description}</p>
                  <div className="flex flex-wrap gap-1 mt-1 sm:mt-2">
                    {factor.competencies.map((comp, i) => (
                      <span key={i} className="inline-block px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                        {comp}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">No resilience factors identified</p>
          )}
        </div>
      </div>

      {/* UAS Events */}
      {data.uasEvents.length > 0 && (
        <div className="mb-3 sm:mb-6">
          <h3 className="text-md font-semibold text-navy mb-2 sm:mb-3">Undesired Aircraft States (UAS)</h3>
          <div className="space-y-1.5 sm:space-y-2">
            {data.uasEvents.map((uas, idx) => (
              <div key={idx} className="p-2 sm:p-3 bg-orange-50 border border-orange-200 rounded">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-orange-800">{uas.description}</span>
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${getSeverityColor(uas.severity)}`}>
                    {uas.severity}
                  </span>
                </div>
                {uas.recovery && (
                  <p className="text-xs text-gray-600 mt-1">
                    <strong>Recovery:</strong> {uas.recovery}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Management Summary */}
      <div className="border-t border-gray-200 pt-3 sm:pt-4">
        <h3 className="text-md font-semibold text-navy mb-2">Management Summary</h3>
        <p className="text-sm text-gray-700 bg-blue-50 p-2 sm:p-3 rounded">
          {data.managementSummary || 'No management summary provided'}
        </p>
      </div>
    </div>
  );
}

export default ORCA360PartB;
